import React, { useState, useRef } from 'react';
import { Upload, X, File, CheckCircle, AlertCircle } from 'lucide-react';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';

const FileUpload = ({
  type = 'image', // 'image', 'video', 'document'
  onUploadComplete,
  accept,
  maxSize = 5, // in MB
  label = 'Upload File'
}) => {

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const getAcceptString = () => {
    if (accept) return accept;
    switch (type) {
      case 'video':
        return 'video/mp4,video/mov,video/avi,video/mkv,video/webm';
      case 'document':
        return '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx';
      case 'image':
      default:
        return 'image/jpeg,image/png,image/gif,image/webp';
    }
  };

  const getMaxSizeBytes = () => {
    switch (type) {
      case 'video':
        return 100 * 1024 * 1024; // 100MB
      case 'document':
        return 10 * 1024 * 1024; // 10MB
      case 'image':
      default:
        return maxSize * 1024 * 1024;
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > getMaxSizeBytes()) {
      const maxSizeMB = type === 'video' ? 100 : type === 'document' ? 10 : maxSize;
      setError(`File size must be less than ${maxSizeMB}MB`);
      toast.error(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append(type, file);

      const { data } = await api.post(`/upload/${type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        }
      });

      if (data.success) {
        setUploadedFile(data.data);
        toast.success('File uploaded successfully');
        if (onUploadComplete) {
          onUploadComplete(data.data);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.message || 'Upload failed');
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setUploadedFile(null);
    setProgress(0);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      {!uploadedFile ? (
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept={getAcceptString()}
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
            id={`file-upload-${type}`}
          />

          <label
            htmlFor={`file-upload-${type}`}
            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploading
              ? 'border-blue-400 bg-blue-50'
              : error
                ? 'border-red-300 bg-red-50 hover:bg-red-100'
                : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              }`}
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-2"></div>
                <p className="text-sm text-blue-600 font-medium">Uploading... {progress}%</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center">
                <AlertCircle className="w-12 h-12 text-red-500 mb-2" />
                <p className="text-sm text-red-600">{error}</p>
                <p className="text-xs text-gray-500 mt-1">Click to try again</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="w-12 h-12 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 font-medium">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {type === 'video' && 'MP4, MOV, AVI, MKV, WEBM (max 100MB)'}
                  {type === 'document' && 'PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX (max 10MB)'}
                  {type === 'image' && `JPG, PNG, GIF, WEBP (max ${maxSize}MB)`}
                </p>
              </div>
            )}
          </label>
        </div>
      ) : (
        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {type === 'image' ? (
                <img
                  src={getFileUrl(uploadedFile.url)}
                  alt="Uploaded"
                  className="w-12 h-12 object-cover rounded"
                />
              ) : (
                <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
                  <File className="w-6 h-6 text-green-600" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {uploadedFile.fileName}
              </p>
              <p className="text-xs text-gray-500">
                {formatFileSize(uploadedFile.fileSize)}
              </p>
            </div>
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          </div>
          <button
            onClick={handleRemove}
            className="ml-4 p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
            title="Remove file"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {uploadedFile && (
        <input type="hidden" value={uploadedFile.url} />
      )}
    </div>
  );
};

export default FileUpload;
